import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';
import { PUBLIC_API_ENDPOINT } from '../../constants/platform';
import { getPublicDonateGoal, DonateGoal } from '../../api/payment/donate-goal.api';
import DonateGoalWidget from '../../components/donate-goal/donate-goal-widget';
import {
  DonateGoalTheme,
  isHexColor,
  parseDonateGoalTheme,
} from '../../components/donate-goal/donate-goal-theme';

/**
 * Overlay THANH TIẾN ĐỘ mục tiêu donate — chèn vào OBS bằng Browser Source.
 *
 * Chạy KHÔNG đăng nhập: lấy mục tiêu qua endpoint công khai, và nghe cùng hub
 * SignalR mà overlay thông báo donate đang dùng (/payment-hubs?userid=...).
 * Mỗi lần có donate thì gọi lại BE để lấy số đúng, thay vì tự cộng ở client
 * (tránh lệch khi rớt mạng hoặc bỏ lỡ sự kiện). Ngoài ra cứ 60 giây tải lại một lần
 * để streamer đổi giao diện trong cài đặt là live tự cập nhật, không phải chép lại link.
 *
 * Link cũ có ?color=RRGGBB&textColor=RRGGBB vẫn chạy: hai màu này đè lên giao diện đã lưu.
 */
const REFRESH_MS = 60000;

const legacyHex = (value: string | null) => {
  const hex = value ? `#${value.replace(/^#/, '')}` : '';
  return isHexColor(hex) ? hex.toUpperCase() : undefined;
};

const PageDonateGoalLivestream = () => {
  const { contentCreatorId } = useParams<{ contentCreatorId: string }>();
  const [searchParams] = useSearchParams();
  const legacyBar = legacyHex(searchParams.get('color'));
  const legacyText = legacyHex(searchParams.get('textColor'));

  const [goal, setGoal] = useState<DonateGoal | null>(null);
  const [donateTick, setDonateTick] = useState(0); // tăng mỗi lần có donate: thẻ nảy + thanh lóe

  const load = useCallback(async () => {
    if (!contentCreatorId) return;
    try {
      setGoal(await getPublicDonateGoal(contentCreatorId));
    } catch {
      // Lỗi mạng: giữ nguyên số đang hiện, lần sau sẽ tải lại.
    }
  }, [contentCreatorId]);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  // OBS tự làm nền trong suốt, nhưng mở thử bằng trình duyệt thường thì nền app sẽ che mất.
  useEffect(() => {
    const { body, documentElement } = document;
    const prevBody = body.style.background;
    const prevHtml = documentElement.style.background;
    body.style.background = 'transparent';
    documentElement.style.background = 'transparent';
    return () => {
      body.style.background = prevBody;
      documentElement.style.background = prevHtml;
    };
  }, []);

  useEffect(() => {
    if (!contentCreatorId) return undefined;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${PUBLIC_API_ENDPOINT}/payment-hubs?userid=${contentCreatorId}`, {
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection
      .start()
      .then(() => {
        connection.on('ReceiveDonationInfo', () => {
          load();
          setDonateTick((n) => n + 1);
        });
      })
      .catch(() => {
        // Không nối được hub: vẫn hiện tiến độ ở lần tải đầu và tải lại định kỳ.
      });

    return () => {
      connection.stop();
    };
  }, [contentCreatorId, load]);

  const theme = useMemo<DonateGoalTheme>(() => {
    const saved = parseDonateGoalTheme(goal?.theme);
    return {
      ...saved,
      ...(legacyBar ? { barFrom: legacyBar, barTo: legacyBar } : {}),
      ...(legacyText ? { text: legacyText } : {}),
    };
  }, [goal?.theme, legacyBar, legacyText]);

  // Chưa đặt mục tiêu / đã tắt -> không vẽ gì để OBS trong suốt hoàn toàn.
  if (!goal?.exists) return null;

  return (
    <div className="flex w-full items-start justify-center bg-transparent p-4">
      <div className="w-full max-w-[560px]">
        <DonateGoalWidget
          size="overlay"
          title={goal.title}
          description={goal.description}
          currentAmount={goal.currentAmount}
          targetAmount={goal.targetAmount}
          donationCount={goal.donationCount}
          isExpired={goal.isExpired}
          isActive={goal.isActive}
          theme={theme}
          animateKey={donateTick}
        />
      </div>
    </div>
  );
};

export default PageDonateGoalLivestream;
